import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Form, Layout, Modal, PageWrap, Spinner, Table } from '@vwfs-bronson/bronson-react';
import { Formik } from 'formik';
import { NumericFormField } from '../../components/FormFields/NumericFormField';
import { calculateQuote } from '../../services/api/miles/miles-services';
import { transformDateFormatWithSlash } from '../../services/common/format';
import { roundNumber } from '../../services/common/formatNew';
import { store } from '../../services/redux';
import { loadingActions, loadingSelectors } from '../../services/redux/features/loading.slice';
import { useNavigate } from '../../services/routing/navigate';
import { calculateTotalDeliveryCost } from '../FormPage/Step1/initial-values-financial';
import { totalCost } from '../utils';
import { DeliveryCostPopupModalSchema } from './validationSchema';

interface DeliveryCostModalProps {
  visible: boolean;
  resetPopupValues: boolean;
  setResetPopupValues: (val) => void;
  onCancel: (val?) => void;
  onConfirm: (val?) => void;
  estDelivery?: string;
  catalogPrice?: number;
  quoteId?: string;
  prefillOptions?: Array<QuoteSchema.Schemas.DeliveryCostComponentCalculateSalesQuoteResponse>;
  isReadonly?: boolean;
}

type InitialValuesType = {
  priceBeforeTaxZero: number;
  priceBeforeTax20: number;
  priceWithTaxZero: number;
  priceWithTax20: number;
  totalCost5: number;
  totalCost20: number;
};

export const DeliveryCostModal: FunctionComponent<DeliveryCostModalProps> = (props) => {
  const formRef: any = useRef();
  const { t } = useTranslation();
  const {
    visible,
    onCancel,
    onConfirm,
    resetPopupValues,
    setResetPopupValues,
    estDelivery,
    catalogPrice,
    quoteId,
    prefillOptions,
    isReadonly,
  } = props;
  const TAX_20_PERCENT: number = 1.2;

  const getOptionPrice = (key: QuoteSchema.Schemas.TaxCodeType): number =>
    Array.isArray(prefillOptions)
      ? (prefillOptions.find((o) => o.costType === 'DELIVERY' && o.deliveryCostAmount?.taxCode === key)
          ?.deliveryCostAmount?.netAmount ?? 0)
      : 0;

  let initialValues: InitialValuesType = {
    priceBeforeTaxZero: 0,
    priceBeforeTax20: 0,
    priceWithTaxZero: 0,
    priceWithTax20: 0,
    totalCost5: 0,
    totalCost20: 0,
  };
  if (Array.isArray(prefillOptions) && prefillOptions.length > 0) {
    const priceBeforeTaxZero = getOptionPrice('NoTaxInland');
    const priceBeforeTax20 = getOptionPrice('FranceInputVATInland20');
    const priceWithTaxZero = priceBeforeTaxZero;
    const priceWithTax20 = roundNumber(priceBeforeTax20 * TAX_20_PERCENT);

    initialValues = {
      priceBeforeTaxZero,
      priceBeforeTax20,
      priceWithTaxZero,
      priceWithTax20,
      totalCost5: priceBeforeTaxZero + priceBeforeTax20,
      totalCost20: priceWithTaxZero + priceWithTax20,
    };
  }

  const [savedValues, setSavedValues] = useState<InitialValuesType>(initialValues);
  const [confirmationDisabled, setConfirmationDisabled] = useState<boolean>(true);
  const navigate = useNavigate();
  const isLoading = useSelector(loadingSelectors.selectLoading);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSubmit = async (values) => {
    /* mock submit */
  };
  const calculateAndSendDeliveryCosts = async () => {
    store.dispatch(loadingActions.setLoading(true));
    const currentValues: InitialValuesType = formRef.current.values;
    setSavedValues(currentValues);

    const request: QuoteSchema.Schemas.CalculateSalesQuoteRequest = {
      financialProduct: {
        ...(estDelivery && {
          contracts: [
            {
              deliveryData: {
                deliveryDate: transformDateFormatWithSlash(estDelivery),
              },
            },
          ],
        }),
        offers: [
          {
            deliveryOptions: [
              {
                deliveryCostComponents: [
                  {
                    isSelected: true,
                    costType: 'DELIVERY',
                    deliveryCostAmount: {
                      taxCode: 'NoTaxInland',
                      netAmount: currentValues.priceBeforeTaxZero,
                    },
                  },
                  {
                    isSelected: true,
                    costType: 'DELIVERY',
                    deliveryCostAmount: {
                      taxCode: 'FranceInputVATInland20',
                      netAmount: currentValues.priceBeforeTax20,
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      ...(catalogPrice && {
        vehicleData: {
          milesDealerVehiclePrice: {
            grossAmount: catalogPrice,
          },
        },
      }),
    };
    try {
      if (!quoteId) {
        throw new Error('Missing quote id!');
      }

      const response = await calculateQuote(quoteId, request);
      const totalCost = calculateTotalDeliveryCost(
        response?.financialProduct?.offers?.[0]?.deliveryOptions?.[0].deliveryCostComponents ?? []
      );
      onConfirm(totalCost);
    } catch {
      navigate.error();
    } finally {
      store.dispatch(loadingActions.setLoading(false));
    }
  };
  return (
    <Modal
      id="deliveryCostModal"
      title={t('deliveryCostModal:title')}
      buttonConfirmText={isReadonly ? null : t('deliveryCostModal:apply')}
      buttonCancelText={t('deliveryCostModal:cancel')}
      shown={visible}
      confirmationDisabled={confirmationDisabled}
      onClose={() => onCancel('close')}
      onCancel={() => {
        onCancel('close');
        formRef.current?.resetForm({ values: savedValues });
      }}
      onConfirm={async () => {
        await calculateAndSendDeliveryCosts();
      }}
      asPortal
      className="c-modal--large"
    >
      <Formik
        initialValues={initialValues}
        innerRef={formRef}
        onSubmit={(values) => onSubmit(values)}
        validationSchema={DeliveryCostPopupModalSchema()}
        enableReinitialize
      >
        {(formProps) => {
          const { values, handleSubmit, setFieldValue, errors } = formProps;
          const handleSubmitOwn = (e) => {
            handleSubmit(e);
          };
          const isValid = Object.keys(errors).length === 0;
          setConfirmationDisabled(!isValid);

          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            setFieldValue('totalCost5', totalCost(values.priceBeforeTaxZero, values.priceBeforeTax20));
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [values.priceBeforeTaxZero, values.priceBeforeTax20]);

          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            setFieldValue('totalCost20', totalCost(values.priceWithTaxZero, values.priceWithTax20));
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [values.priceWithTaxZero, values.priceWithTax20]);

          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            if (resetPopupValues) {
              formRef.current?.resetForm({ values: initialValues });
              setSavedValues(initialValues);
              setResetPopupValues(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [resetPopupValues]);
          return (
            <Form onSubmit={handleSubmitOwn}>
              <PageWrap>
                <Layout>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th />
                        <Table.Th>{t('deliveryCostModal:tva')}</Table.Th>
                        <Table.Th>{t('deliveryCostModal:priceBeforeTax')}</Table.Th>
                        <Table.Th>{t('deliveryCostModal:priceAfterTax')}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      <Table.Tr>
                        <Table.Td>{t('deliveryCostModal:accReducedVat')}</Table.Td>
                        <Table.Td>{t('deliveryCostModal:zeroPercentage')}</Table.Td>
                        <Table.Td>
                          <NumericFormField
                            isDisabled={isReadonly}
                            fieldName="priceBeforeTaxZero"
                            type="money"
                            maximumFractionDigits={2}
                            onValueChange={(value) => setFieldValue('priceWithTaxZero', value)}
                          />
                        </Table.Td>
                        <Table.Td>
                          <NumericFormField
                            isDisabled={isReadonly}
                            fieldName="priceWithTaxZero"
                            type="money"
                            maximumFractionDigits={2}
                            onValueChange={(value) => setFieldValue('priceBeforeTaxZero', value)}
                          />
                        </Table.Td>
                      </Table.Tr>

                      <Table.Tr>
                        <Table.Td>{t('deliveryCostModal:accWithVat')}</Table.Td>
                        <Table.Td>{t('deliveryCostModal:twentyPercentage')}</Table.Td>
                        <Table.Td>
                          <NumericFormField
                            fieldName="priceBeforeTax20"
                            isDisabled={isReadonly}
                            type="money"
                            maximumFractionDigits={2}
                            onValueChange={(value) =>
                              setFieldValue('priceWithTax20', roundNumber(value * TAX_20_PERCENT))
                            }
                          />
                        </Table.Td>
                        <Table.Td>
                          <NumericFormField
                            isDisabled={isReadonly}
                            fieldName="priceWithTax20"
                            type="money"
                            maximumFractionDigits={2}
                            onValueChange={(value) =>
                              setFieldValue('priceBeforeTax20', roundNumber(value / TAX_20_PERCENT))
                            }
                          />
                        </Table.Td>
                      </Table.Tr>

                      <Table.Tr>
                        <Table.Td>{t('deliveryCostModal:total')}</Table.Td>
                        <Table.Td />
                        <Table.Td>
                          <NumericFormField
                            fieldName="totalCost5"
                            type="money"
                            maximumFractionDigits={2}
                            isReadonly={!isReadonly}
                            isDisabled={isReadonly}
                          />
                        </Table.Td>
                        <Table.Td>
                          <NumericFormField
                            fieldName="totalCost20"
                            type="money"
                            maximumFractionDigits={2}
                            isReadonly={!isReadonly}
                            isDisabled={isReadonly}
                          />
                        </Table.Td>
                      </Table.Tr>
                    </Table.Tbody>
                  </Table>
                </Layout>
              </PageWrap>
            </Form>
          );
        }}
      </Formik>
      {/* TODO Remove when Bronson fixes BRONSD-1042 */}
      {isLoading && <Spinner fullPage />}
    </Modal>
  );
};
